import { Fragment, ReactNode, useState } from "react";
import CopyButtonWithFeedback from "../../../../common/core-ui/CopyButtonWithFeedback.tsx";
import { clsx } from "clsx";
import EditCopy from "../../../../common/icons/EditCopy.tsx";
import AssetFeatureTags from "../../../../common/features/asset/AssetFeatureTags.tsx";
import { getReaderURL } from "../../../bookshelfLinks.ts";
import { useIntl } from "react-intl";
import {
  canShowCoursewareDetails,
  isExploreAsset,
  isManageAsset,
} from "../../../../common/features/asset/assetHelpers.ts";
import CoursewareDialog from "../../actions/courseware/CoursewareDialog.tsx";
import styles from "./BasicAssetFields.module.scss";
import {
  License,
  ManageAsset,
} from "../../../../common/api/decimal/common-types/manageAssets.ts";

export default function BasicAssetFields({
  asset,
  license,
}: {
  asset: ManageAsset | VST.Bridge.Asset;
  license?: License;
}) {
  const intl = useIntl();
  const [isCoursewareDialogOpen, setIsCoursewareDialogOpen] = useState(false);

  const fields: {
    label: string;
    value: ReactNode;
    copyable?: boolean;
  }[] = [
    {
      label: intl.formatMessage({ defaultMessage: "Author(s)" }),
      value: asset.author,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Publisher" }),
      value: asset.publisher,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Copyright" }),
      value: isManageAsset(asset) ? asset.copyrightDate : asset.publishDate,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Format" }),
      value: isExploreAsset(asset)
        ? undefined
        : asset.vbkType === "epub"
        ? "ePub"
        : asset.vbkType,
    },
    {
      label: intl.formatMessage({ defaultMessage: "VBID" }),
      value: asset.vbid,
    },
    {
      label: intl.formatMessage({ defaultMessage: "URL" }),
      value: getReaderURL(asset.vbid),
      copyable: true,
    },
  ].filter((el) => !!el.value);

  if (
    license &&
    isManageAsset(asset) &&
    canShowCoursewareDetails(asset, license)
  ) {
    fields.push({
      label: intl.formatMessage({ defaultMessage: "Access Code" }),
      value: (
        <button
          onClick={() => setIsCoursewareDialogOpen(true)}
          className="vst-link vst-link-size-inherit"
        >
          {license.accessCode?.revealed
            ? intl.formatMessage({ defaultMessage: "View" })
            : intl.formatMessage({ defaultMessage: "View Instructions" })}
        </button>
      ),
    });
  }

  return (
    <div
      className={clsx(
        "vst-definition-list",
        "vst-definition-list-size-small",
        styles.section
      )}
    >
      <h3>{intl.formatMessage({ defaultMessage: "Book Details" })}</h3>
      <dl>
        {fields.map(({ label, value, copyable }) => (
          <Fragment key={label}>
            <dt>{label}</dt>
            <dd>
              {value}
              {copyable && typeof value === "string" && (
                <CopyButtonWithFeedback
                  aria-label={intl.formatMessage({
                    defaultMessage: "Copy URL to clipboard",
                  })}
                  defaultTooltip={intl.formatMessage({
                    defaultMessage: "Copy",
                  })}
                  textToCopy={value}
                  className={clsx(
                    "vst-icon-button-circle",
                    "vst-icon-button-variant-cloud",
                    "vst-icon-button-size-small",
                    styles.copyButton
                  )}
                >
                  <EditCopy />
                </CopyButtonWithFeedback>
              )}
            </dd>
          </Fragment>
        ))}
        {isManageAsset(asset) && (
          <>
            <dt
              className={clsx(
                styles.fullWidthCell,
                styles.additionalFeaturesHeader
              )}
            >
              {intl.formatMessage({ defaultMessage: "Additional Features" })}
            </dt>
            <dd className={styles.fullWidthCell}>
              <AssetFeatureTags asset={asset} />
            </dd>
          </>
        )}
      </dl>
      {isManageAsset(asset) && isCoursewareDialogOpen && (
        <CoursewareDialog
          asset={asset}
          license={license!}
          onRequestClose={() => setIsCoursewareDialogOpen(false)}
        />
      )}
    </div>
  );
}
